import { useTranslation } from 'react-i18next';
import { cx } from './cx';

/**
 * The four sub-brand variants that have an inline `logo / Sub-brand`
 * lockup. The `app` value resolves to the `app.subBrand.<app>` key in
 * `packages/i18n/src/locales/en/common.json`:
 *
 *   - `shop`        → "Shop"
 *   - `sell`        → "Sell"
 *   - `admin`       → "Admin"
 *   - `backOffice`  → "Back office"
 *
 * If you need a new sub-brand, add the key to the catalog and run
 * `pnpm --filter @pc/i18n run translate` before pinning the variant
 * here.
 */
export type BrandLockupApp = 'shop' | 'sell' | 'admin' | 'backOffice';

export interface BrandLockupProps {
  app: BrandLockupApp;
  /** Defaults to `/`. */
  href?: string;
  className?: string;
}

/**
 * The platform PeptideClients logo. Exported so other surfaces (e.g. the
 * public calendar /c/<token> page) can fall back to it when a store has
 * not uploaded its own `core.store_settings.logo_url`.
 */
export const PLATFORM_LOGO_URL =
  'https://bxbdqmtldkhgwpnwecox.supabase.co/storage/v1/object/public/Public%20Photos/LOGOUpdated.png';

const LOGO_URL = PLATFORM_LOGO_URL;

/**
 * Inline `[logo] / <Sub-brand>` lockup. Byte-for-byte port of the
 * `.nav-logo` + `.nav-logo-suffix` pattern in
 * `apps/marketplace/src/styles/global.css` (lines 112-141 and 276-278)
 * so every PeptideClients surface that wears a sub-brand reads as one
 * family.
 *
 * Spec, locked:
 *   - Container: `inline-flex; align-items: center; gap: 10px;
 *                 line-height: 0; color: var(--ink)`
 *   - Logo height: 28px below 720px, 34px at md and up
 *   - Suffix: Fraunces 400, 0.88rem mobile / 0.95rem desktop,
 *             color var(--muted) (#6E6051), letter-spacing -0.005em,
 *             line-height 1, white-space nowrap
 *   - "/" separator: var(--muted-2) (#8C7E6C), margin-right 8px
 *   - Suffix (and its "/") collapse entirely below 420px so the logo
 *     alone stays comfortable on a narrow phone
 *
 * Marketplace's source uses a `::before` pseudo-element for the slash;
 * here we render a real `<span aria-hidden>` so the muted-2 colour and
 * the 0.88rem/0.95rem responsive sizing all live in Tailwind classes
 * the consumer can see. The rendered output is byte-identical.
 */
export function BrandLockup({ app, href = '/', className }: BrandLockupProps) {
  const { t } = useTranslation();
  const subBrand = t(`app.subBrand.${app}`);
  return (
    <a
      href={href}
      aria-label={`PeptideClients ${subBrand} home`}
      className={cx(
        'inline-flex items-center gap-[10px] leading-none text-ink min-w-0 no-underline',
        className,
      )}
    >
      <img
        src={LOGO_URL}
        alt="PeptideClients"
        width={160}
        height={36}
        className="h-7 md:h-[34px] w-auto block"
      />
      <span
        aria-hidden
        className="hidden min-[421px]:inline font-display font-normal text-[0.88rem] md:text-[0.95rem] tracking-[-0.005em] leading-none whitespace-nowrap text-muted"
      >
        <span className="text-muted-2 mr-2">/</span>
        {subBrand}
      </span>
    </a>
  );
}
